import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPublishedCatalogs, getSelectedCatalog } from '../../common/utils/catalogUtils.js';
import { deepEqual } from '../../common/utils/objectUtils.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { useSessionData } from '../../public/site/SessionDataProvider.js';
import { useOnlineModelsContext } from '../../public/site/onlineModel/onlineModelContextProvider.js';
import { addToCart } from '../../selfservice/actions/index.js';
import { ProductDetails } from './ProductDetails.js';
import { isEmployeeUser } from './utils/productDetailsEmployeeUtils.js';
import type { CartItemContainer } from './ProductDetails.js';
import type { Category } from '../../common/types/category.js';
import type { Catalog, OnlineModel } from '../../generated/api/models.js';
import type { State } from '../../selfservice/common/store.js';

import './ProductDetailsEmployeeLoader.scss';

interface ProductDetailsEmployeeLoaderProps {
  onlineModel: OnlineModel;
  category: Category;
}

export const ProductDetailsEmployeeLoader = (props: ProductDetailsEmployeeLoaderProps) => {
  const dispatch = useDispatch();
  const { onlineModel } = props;
  const { authenticatedUser } = useAuth();
  const { priceAlteredModels, fetchModelsByCodes } = useOnlineModelsContext();
  const { selectedCatalogCode, virtualCatalogItems } = useSelector(
    (state: State) => ({
      selectedCatalogCode: state.selfservice?.virtualCatalogs?.selectedCatalogCode,
      virtualCatalogItems: state.selfservice?.virtualCatalogs?.items,
    }),
    deepEqual
  );
  const companyInfo = useSessionData().companyInfo;
  const catalogs: Catalog[] = getPublishedCatalogs(virtualCatalogItems) || [];
  const catalog = getSelectedCatalog(catalogs, selectedCatalogCode);
  const privateOnlineModel = priceAlteredModels.onlineModelsByCode?.[onlineModel.onlineModelCode];

  useEffect(() => {
    if (authenticatedUser?.activeAccountMasterId && onlineModel.onlineModelCode) {
      fetchModelsByCodes([onlineModel.onlineModelCode]);
    }
  }, [authenticatedUser?.activeAccountMasterId, fetchModelsByCodes, onlineModel.onlineModelCode]);

  const resolvedOnlineModel = privateOnlineModel ?? onlineModel;

  return (
    <ProductDetails
      addToCart={(cartItemContainer: CartItemContainer) => dispatch(addToCart(cartItemContainer))}
      isPublicSite={false}
      isEmployee={isEmployeeUser(authenticatedUser)}
      onlineModel={resolvedOnlineModel}
      user={authenticatedUser}
      catalog={catalog}
      showOfferAndPrices={Boolean(privateOnlineModel)}
      companyInfo={companyInfo}
    />
  );
};
